export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export class JsonValidator {
  // Cache for parsed JSON to avoid re-parsing
  private static parseCache = new Map<string, any>()
  private static cacheSize = 100

  static validate(jsonString: string): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Check if string is empty
    if (!jsonString.trim()) {
      return {
        isValid: true,
        errors: [],
        warnings: []
      }
    }

    // Basic JSON syntax validation with caching
    let parsed: any
    try {
      // Check cache first
      if (this.parseCache.has(jsonString)) {
        parsed = this.parseCache.get(jsonString)
      } else {
        parsed = JSON.parse(jsonString)
        // Cache the result
        this.cacheParseResult(jsonString, parsed)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON syntax'
      errors.push(`Syntax Error: ${errorMessage}`)
      return {
        isValid: false,
        errors,
        warnings
      }
    }

    // Early return if we have syntax errors
    if (errors.length > 0) {
      return {
        isValid: false,
        errors,
        warnings
      }
    }

    // Additional linting checks
    this.performLintingChecks(jsonString, errors, warnings)

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  private static cacheParseResult(jsonString: string, parsed: any): void {
    // Implement LRU cache
    if (this.parseCache.size >= this.cacheSize) {
      const firstKey = this.parseCache.keys().next().value
      if (firstKey) {
        this.parseCache.delete(firstKey)
      }
    }
    this.parseCache.set(jsonString, parsed)
  }

  private static performLintingChecks(jsonString: string, errors: string[], warnings: string[]): void {
    // Early return for very large files to improve performance
    if (jsonString.length > 1000000) { // 1MB
      this.checkFileSize(jsonString, warnings)
      return
    }

    const lines = jsonString.split('\n')
    
    // Parallel processing for better performance
    const checks = [
      () => this.checkTrailingCommas(lines, errors, warnings),
      () => this.checkIndentation(lines, warnings),
      () => this.checkEmptyStructures(jsonString, warnings),
      () => this.checkDuplicateKeys(jsonString, errors),
      () => this.checkFileSize(jsonString, warnings),
      () => this.checkNestedDepth(jsonString, warnings)
    ]

    // Run checks sequentially to avoid overwhelming the main thread
    checks.forEach(check => check())
  }

  private static checkTrailingCommas(lines: string[], errors: string[], warnings: string[]): void {
    // Use more efficient regex patterns
    const trailingCommaRegex = /,\s*[}\]],\s*$/
    const propertyRegex = /"[^"]*"\s*:\s*[^,}\]]+$/
    const nextPropertyRegex = /^"[^"]*"\s*:/

    for (let i = 0; i < lines.length; i++) {
      const trimmedLine = lines[i].trim()
      
      // Check for trailing comma before closing brace/bracket
      if (trailingCommaRegex.test(trimmedLine)) {
        warnings.push(`Line ${i + 1}: Trailing comma detected`)
      }
      
      // Check for missing comma between properties
      if (propertyRegex.test(trimmedLine)) {
        const nextLine = lines[i + 1]?.trim()
        if (nextLine && nextPropertyRegex.test(nextLine) && !trimmedLine.endsWith(',')) {
          const currentLineContent = trimmedLine.replace(/"[^"]*"\s*:\s*/, '')
          const nextLineContent = nextLine.replace(/^"[^"]*"\s*:\s*/, '')
          
          if (!this.isComplexValue(currentLineContent) && !this.isComplexValue(nextLineContent)) {
            warnings.push(`Line ${i + 1}: Missing comma after property`)
          }
        }
      }
    }
  }

  private static isComplexValue(content: string): boolean {
    const trimmed = content.trim()
    
    // More efficient checks
    if (trimmed.startsWith('{') && !trimmed.endsWith('}')) return true
    if (trimmed.startsWith('[') && !trimmed.endsWith(']')) return true
    if (trimmed.startsWith('"') && !trimmed.endsWith('"')) return true
    
    return false
  }

  private static checkIndentation(lines: string[], warnings: string[]): void {
    let indentType: 'spaces' | 'tabs' | 'mixed' | null = null
    let indentSize = 0

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const trimmed = line.trim()
      
      if (!trimmed || trimmed.startsWith('//')) continue
      
      const leadingWhitespace = line.slice(0, line.indexOf(trimmed))
      
      if (leadingWhitespace.length === 0) continue
      
      const hasTabs = leadingWhitespace.includes('\t')
      const hasSpaces = leadingWhitespace.includes(' ')
      
      if (hasTabs && hasSpaces) {
        if (indentType !== 'mixed') {
          indentType = 'mixed'
          warnings.push(`Line ${i + 1}: Mixed indentation detected`)
        }
      } else if (hasTabs) {
        if (indentType === null) {
          indentType = 'tabs'
        } else if (indentType === 'spaces') {
          indentType = 'mixed'
          warnings.push(`Line ${i + 1}: Mixed indentation detected`)
        }
      } else if (hasSpaces) {
        const spaces = leadingWhitespace.length
        if (indentType === null) {
          indentType = 'spaces'
          indentSize = spaces
        } else if (indentType === 'tabs') {
          indentType = 'mixed'
          warnings.push(`Line ${i + 1}: Mixed indentation detected`)
        } else if (indentType === 'spaces' && spaces % indentSize !== 0) {
          warnings.push(`Line ${i + 1}: Inconsistent indentation`)
        }
      }
    }
  }

  private static checkEmptyStructures(jsonString: string, warnings: string[]): void {
    // More efficient regex patterns
    const emptyObjectRegex = /\{\s*\}/g
    const emptyArrayRegex = /\[\s*\]/g
    
    let match
    while ((match = emptyObjectRegex.exec(jsonString)) !== null) {
      const lineNumber = jsonString.substring(0, match.index).split('\n').length
      warnings.push(`Line ${lineNumber}: Empty object detected`)
    }
    
    while ((match = emptyArrayRegex.exec(jsonString)) !== null) {
      const lineNumber = jsonString.substring(0, match.index).split('\n').length
      warnings.push(`Line ${lineNumber}: Empty array detected`)
    }
  }

  private static checkDuplicateKeys(jsonString: string, errors: string[]): void {
    try {
      const parsed = JSON.parse(jsonString)
      const duplicates = this.findDuplicateKeys(parsed)
      
      if (duplicates.length > 0) {
        errors.push(`Duplicate keys found: ${duplicates.join(', ')}`)
      }
    } catch {
      // If parsing fails, skip duplicate key check
    }
  }

  private static findDuplicateKeys(obj: any, path: string = '', duplicates: string[] = []): string[] {
    if (typeof obj !== 'object' || obj === null) {
      return duplicates
    }

    const keys = Object.keys(obj)
    const seen = new Set<string>()

    for (const key of keys) {
      const fullPath = path ? `${path}.${key}` : key
      
      if (seen.has(key)) {
        duplicates.push(fullPath)
      } else {
        seen.add(key)
      }

      // Recursively check nested objects
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        this.findDuplicateKeys(obj[key], fullPath, duplicates)
      }
    }

    return duplicates
  }

  private static checkFileSize(jsonString: string, warnings: string[]): void {
    const sizeInKB = jsonString.length / 1024
    const sizeInMB = sizeInKB / 1024
    
    if (sizeInMB > 5) {
      warnings.push(`Large JSON file detected (${sizeInMB.toFixed(1)}MB) - consider splitting into smaller files for optimal performance`)
    } else if (sizeInMB > 1) {
      warnings.push(`Large JSON file detected (${sizeInMB.toFixed(1)}MB) - performance may be impacted`)
    }
  }

  private static checkNestedDepth(jsonString: string, warnings: string[]): void {
    let maxDepth = 0
    let currentDepth = 0
    
    for (const char of jsonString) {
      if (char === '{' || char === '[') {
        currentDepth++
        maxDepth = Math.max(maxDepth, currentDepth)
      } else if (char === '}' || char === ']') {
        currentDepth--
      }
    }
    
    if (maxDepth > 10) {
      warnings.push(`Deep nesting detected (${maxDepth} levels) - consider flattening the structure`)
    }
  }

  // Clear cache when needed
  static clearCache(): void {
    this.parseCache.clear()
  }

  // Additional utility methods
  static isValidJson(jsonString: string): boolean {
    try {
      JSON.parse(jsonString)
      return true
    } catch {
      return false
    }
  }

  static getJsonSize(jsonString: string): number {
    return new Blob([jsonString]).size
  }

  static getJsonStats(jsonString: string): {
    lines: number
    characters: number
    properties: number
    depth: number
  } {
    const lines = jsonString.split('\n').length
    const characters = jsonString.length
    
    let properties = 0
    let depth = 0
    
    try {
      const parsed = JSON.parse(jsonString)
      const stats = this.calculateStats(parsed)
      properties = stats.properties
      depth = stats.depth
    } catch {
      // If parsing fails, estimate properties by counting colons
      properties = (jsonString.match(/:/g) || []).length
    }
    
    return {
      lines,
      characters,
      properties,
      depth
    }
  }

  private static calculateStats(obj: any, currentDepth: number = 0): { properties: number; depth: number } {
    if (typeof obj !== 'object' || obj === null) {
      return { properties: 0, depth: currentDepth }
    }

    let totalProperties = Object.keys(obj).length
    let maxDepth = currentDepth

    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && value !== null) {
        const childStats = this.calculateStats(value, currentDepth + 1)
        totalProperties += childStats.properties
        maxDepth = Math.max(maxDepth, childStats.depth)
      }
    }

    return {
      properties: totalProperties,
      depth: maxDepth
    }
  }
}
